import { assign, createMachine } from 'xstate';

import { GetSsoToken } from './sso';

type LoginLogoutMachineContext = {
  error?: unknown;
};

type LoginLogoutMachineEvent =
  | {
      type: 'report sso token obtained';
      ssoUserId: number;
      token: string;
      callback?: (userId: number, token: string) => void;
    }
  | {
      type: 'log in';
      credentials: { email: string; password: string };
      callback?: (userId: number, token: string) => void;
    }
  | { type: 'log out' }
  | {
      type: 'error.platform.login logout.logging in:invocation[0]';
      data: unknown;
    };

type LoginLogoutMachineState =
  | { value: 'logging in'; context: { error: undefined } }
  | { value: 'logged in'; context: { error: undefined } }
  | { value: 'logged out'; context: { error?: unknown } };

export function createLoginLogoutMachine({
  getSsoToken,
  notifyOfLoggedIn,
  notifyOfLoggedOut,
  removeSsoTokenFromLocalStorage,
  storeSsoTokenInLocalStorage,
}: {
  getSsoToken: (
    params: Omit<Parameters<GetSsoToken>[0], 'apiBaseUrl' | 'product'>,
  ) => ReturnType<GetSsoToken>;
  notifyOfLoggedIn: () => void;
  notifyOfLoggedOut: () => void;
  removeSsoTokenFromLocalStorage: () => void;
  storeSsoTokenInLocalStorage: ({
    ssoUserId,
    token,
  }: {
    ssoUserId: number;
    token: string;
  }) => void;
}) {
  return createMachine<
    LoginLogoutMachineContext,
    LoginLogoutMachineEvent,
    LoginLogoutMachineState
  >(
    {
      id: 'login logout',
      predictableActionArguments: true,
      preserveActionOrder: true,
      initial: 'logged out',
      context: {},
      on: {
        'log out': {
          actions: ['removeSsoTokenFromLocalStorage', 'notifyOfLoggedOut'],
          target: 'logged out',
        },
        'log in': {
          actions: ['removeError'],
          target: 'logging in',
        },
      },
      states: {
        'logging in': {
          invoke: {
            src: 'getSsoToken',
            onError: {
              actions: ['storeError'],
              target: 'logged out',
            },
          },
          on: {
            'report sso token obtained': {
              actions: ['notifyOfLoggedIn'],
              target: 'logged in',
            },
          },
        },
        'logged in': {},
        'logged out': {},
      },
    },
    {
      services: {
        getSsoToken: (context, event) => async (send) => {
          if (event.type !== 'log in') {
            throw new Error('Unable to get SSO token without credentials');
          }

          const { ssoUserId, token } = await getSsoToken({
            credentials: event.credentials,
          });

          storeSsoTokenInLocalStorage({
            ssoUserId: ssoUserId,
            token: token,
          });

          await event.callback?.(ssoUserId, token);

          send({
            type: 'report sso token obtained',
            ssoUserId,
            token,
          });
        },
      },
      actions: {
        notifyOfLoggedIn: (context, event) => {
          if (event.type === 'report sso token obtained') {
            notifyOfLoggedIn();
          }
        },
        notifyOfLoggedOut: () => notifyOfLoggedOut(),
        removeSsoTokenFromLocalStorage: () => removeSsoTokenFromLocalStorage(),
        removeError: assign((context) => {
          return {
            ...context,
            error: undefined,
          };
        }),
        storeError: assign((context, event) => {
          if (
            event.type ===
            'error.platform.login logout.logging in:invocation[0]'
          ) {
            return {
              ...context,
              error: event.data,
            };
          }

          return context;
        }),
      },
    },
  );
}
